import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAnchor, MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from "@angular/material/tabs";
import { EventAdminDetailDto, PointAdminDto, PointAdminSummaryDto } from '@aoe/client';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { DialogService } from 'src/app/core/services/dialog.service';
import { selectEventAdminSelected } from 'src/app/core/store/admin/event-admin/event-admin.selector';
import { PointAdminActions } from 'src/app/core/store/admin/point/poinAdmin.actions';
import { selectSelectedPoint } from 'src/app/core/store/admin/point/pointAdmin.selector';
import { IconsEnum } from 'src/app/shared/enum/Icons.enum';
import { RaceLinkedComponent } from "./race-linked/race-linked.component";

@Component({
  selector: 'aoe-point-admin-dialog',
  templateUrl: './point-admin-dialog.component.html',
  styleUrls: ['./point-admin-dialog.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatAnchor,
    MatButtonModule,
    MatTabsModule,
    RaceLinkedComponent
  ]
})
export class PointAdminDialogComponent implements OnInit {

  Icons = IconsEnum;

  fg!: FormGroup;
  point?: PointAdminDto | null = null;
  event!: EventAdminDetailDto;


  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: { point?: PointAdminSummaryDto, eventId: string },
    private readonly dialogService: DialogService,
    private readonly store: Store,
    private readonly formBuilder: FormBuilder
  ) { }
  ngOnInit(): void {
    // 1. Initialisation de la structure du formulaire
    this.initFromData();

    // 2. Chargement de l'événement
    this.store.select(selectEventAdminSelected)
      .pipe(filter(e => !!e), take(1))
      .subscribe(event => this.event = event);

    // 3. Mode ÉDITION vs Mode CRÉATION
    if (this.data.point?.id) {
      // Mode ÉDITION : On va chercher les détails et on écoute le Store
      this.store.dispatch(PointAdminActions.loadPointById({
        eventId: this.data.eventId,
        payload: { id: this.data.point.id }
      }));

      this.store.select(selectSelectedPoint)
        .pipe(filter(p => !!p), take(1))
        .subscribe(p => {
          this.point = p;
          this.initFromData();
          this.patchForm(this.point);
        });
    } else {
      // Mode CRÉATION : Réinitialisation du point sélectionné dans le Store et formulaire vierge
      // this.store.dispatch(PointAdminActions.);
      // Optionnel : si vous avez des valeurs par défaut dans le formulaire
      // this.patchFormForCreation();
    }
  }

  initFromData() {
    this.fg = this.formBuilder.group({
      // cpid: tinyint, Nullable, Défaut: NULL
      cpid: new FormControl<number | null>(this.point?.cpid ?? null, [Validators.required, Validators.min(0)]),

      // name: varchar(100), NON Nullable, Requis
      name: new FormControl<string>(this.point?.name ?? '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100)]
      }),

      // is_prefinish: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      isPrefinish: new FormControl<boolean>(!!this.point?.prefinish, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // is_finish: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      isFinish: new FormControl<boolean>(!!this.point?.finish, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // is_start: tinyint(1), Nullable, Défaut: 0 (boolean)
      isStart: new FormControl<boolean | null>(!!this.point?.start),

      // in_out: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      inOut: new FormControl<boolean>(!!this.point?.inOut, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // coordinates: longtext, Nullable, Défaut: NULL (ex: JSON ou GeoJSON)
      coordinates: this.formBuilder.group({
        lat: new FormControl<number | null>(this.point?.coordinates?.lat ?? null),
        lng: new FormControl<number | null>(this.point?.coordinates?.lng ?? null)
      }),

      // refreshment: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      refreshment: new FormControl<boolean>(!!this.point?.options?.refreshment, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // toilet: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      toilet: new FormControl<boolean>(!!this.point?.options?.toilet, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // medical: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      medical: new FormControl<boolean>(!!this.point?.options?.medical, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // parking: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      parking: new FormControl<boolean>(!!this.point?.options?.parking, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // shower: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      shower: new FormControl<boolean>(!!this.point?.options?.shower, {
        nonNullable: true,
        validators: [Validators.required]
      }),

      // bus: tinyint(1), NON Nullable, Défaut: 0 (boolean)
      bus: new FormControl<boolean>(!!this.point?.options?.bus, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      attachedRaces: this.formBuilder.array([])
    });
  }

  get name() {
    return this.fg?.get('name')?.value
  }

  /** Pré-remplissage du formulaire et reconstruction du FormArray */
  private patchForm(point: PointAdminDto): void {

    const attachedRacesArray = this.fg.get('attachedRaces') as any;
    attachedRacesArray.clear();

    // Reconstitution des sous-FormGroup pour chaque liaison course
    if (point.races && point.races.length > 0) {
      point.races.forEach((link) => {
        attachedRacesArray.push(
          this.formBuilder.group({
            raceId: [link.raceId, [Validators.required]],
            nbPassage: [link.nbPassage ?? 1, [Validators.required, Validators.min(1)]],
            distanceFromStart: [link.distance ?? 0, [Validators.required, Validators.min(0)]],
            elevation: [link.elevation.elevation ?? 0, [Validators.required]],
            elevationFromStart: [link.elevation.fromStart ?? 0, [Validators.required, Validators.min(0)]],
            openingDate: [link.date.start ?? null],
            closingDate: [link.date.end ?? null]
          })
        );
      });
    }
  }

  submit() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    }

    const payload = this.fg.getRawValue();

    if (this.point?.id) {
      // Mode édition : mise à jour
      this.store.dispatch(
        PointAdminActions.updatePoint({
          payload,
          eventId: this.event.id,
          id: this.point.id
        })
      );
    } else {
      // Mode création : nouvel enregistrement
      this.store.dispatch(
        PointAdminActions.createPoint({
          payload: {
            eid: this.event.eid,
            ...payload
          },
          eventId: this.event.id,
        })
      );
    }

  }


  close(): void {
    this.dialogService.closePointAdminModal();
  }

}
